<template>
  <div>
  <NavBar />
  <section class="admin-block-user">
    <div class="card-container">
      <div class="card">
        <h1>Block / Unblock User</h1>
        <p class="card-subtitle">
          Enter a user's email to check their status and block or unblock them from the entire system.
        </p>

        <form @submit.prevent="lookupUser">
          <label for="block-email">Email</label>
          <input
            id="block-email"
            type="email"
            v-model.trim="email"
            placeholder="user@example.com"
            required
          />
          <button type="submit" :disabled="isLoading || !email">
            <span v-if="isLoading" class="spinner"></span>
            {{ isLoading ? 'Looking up…' : 'Look up user' }}
          </button>
        </form>

        <div v-if="userStatus" class="status-box" :class="userStatus.is_blocked ? 'blocked' : 'active'">
          <p>
            <strong>{{ userStatus.email }}</strong> is currently
            <strong>{{ userStatus.is_blocked ? 'BLOCKED' : 'ACTIVE' }}</strong>.
          </p>
          <button
            :class="userStatus.is_blocked ? 'btn-unblock' : 'btn-block'"
            :disabled="isActing"
            @click="toggleBlock"
          >
            <span v-if="isActing" class="spinner"></span>
            {{ userStatus.is_blocked ? 'Unblock user' : 'Block user' }}
          </button>
        </div>

        <p v-if="successMessage" class="status success">{{ successMessage }}</p>
        <p v-if="errorMessage" class="status error">{{ errorMessage }}</p>
      </div>
    </div>
  </section>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import NavBar from '@/components/NavBar';
import manageRidePrivacyApi from '@/api/manageRidePrivacy';
import { INTERNAL_HOME_PATH } from '@/common/appHome';

export default {
  name: 'AdminBlockUser',
  components: { NavBar },
  data() {
    return {
      email: '',
      userStatus: null,
      isLoading: false,
      isActing: false,
      successMessage: '',
      errorMessage: '',
    };
  },
  computed: {
    ...mapGetters(['userId', 'apptoken']),
  },
  created() {
    if (this.userId !== 1) {
      this.$router.push(INTERNAL_HOME_PATH);
    }
  },
  methods: {
    async lookupUser() {
      this.isLoading = true;
      this.userStatus = null;
      this.successMessage = '';
      this.errorMessage = '';
      try {
        const res = await manageRidePrivacyApi.getUserBlockStatus(this.apptoken, this.email);
        if (res.data.status === 'success') {
          this.userStatus = res.data;
        } else if (res.data.status === 'not_found') {
          this.errorMessage = 'User not found.';
        } else {
          this.errorMessage = 'Error looking up user.';
        }
      } catch {
        this.errorMessage = 'Error looking up user.';
      } finally {
        this.isLoading = false;
      }
    },
    async toggleBlock() {
      this.isActing = true;
      this.successMessage = '';
      this.errorMessage = '';
      const newBlock = !this.userStatus.is_blocked;
      try {
        const res = await manageRidePrivacyApi.setUserBlocked(this.apptoken, this.userStatus.email, newBlock);
        if (res.data.status === 'success') {
          this.userStatus.is_blocked = res.data.is_blocked;
          this.successMessage = newBlock ? 'User has been blocked.' : 'User has been unblocked.';
        } else {
          this.errorMessage = 'Failed to update user status.';
        }
      } catch {
        this.errorMessage = 'Failed to update user status.';
      } finally {
        this.isActing = false;
      }
    },
  },
};
</script>

<style scoped>
.admin-block-user {
  display: flex;
  justify-content: center;
  padding: 80px 16px 40px;
}

.card-container {
  width: 100%;
  max-width: 480px;
}

.card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.1);
  padding: 32px;
}

h1 {
  font-size: 22px;
  margin-bottom: 8px;
}

.card-subtitle {
  color: #666;
  font-size: 14px;
  margin-bottom: 24px;
}

label {
  display: block;
  font-weight: 600;
  margin-bottom: 6px;
  font-size: 14px;
}

input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 15px;
  margin-bottom: 14px;
  box-sizing: border-box;
}

button[type="submit"] {
  width: 100%;
  padding: 10px;
  background: #007bff;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 15px;
  cursor: pointer;
}

button[type="submit"]:disabled {
  background: #aac5e8;
  cursor: not-allowed;
}

.status-box {
  margin-top: 24px;
  padding: 16px;
  border-radius: 6px;
}

.status-box.active {
  background: #e6f4ea;
  border: 1px solid #81c784;
}

.status-box.blocked {
  background: #fdecea;
  border: 1px solid #e57373;
}

.status-box p {
  margin-bottom: 12px;
  font-size: 15px;
}

.btn-block {
  padding: 8px 20px;
  background: #dc3545;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
}

.btn-unblock {
  padding: 8px 20px;
  background: #28a745;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
}

.btn-block:disabled,
.btn-unblock:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.status {
  margin-top: 16px;
  font-size: 14px;
  font-weight: 600;
}

.status.success { color: #2e7d32; }
.status.error { color: #c62828; }

.spinner {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid rgba(255,255,255,0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  margin-right: 6px;
  vertical-align: middle;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
